import React from "react";
import Head from "next/head";
import Link from "next/link";
import Navigation from "@/layout/navigation";
import WhoWeServeFeatureSection from "@/components/who-we-serve/WhoWeServeFeatureSection";

export default function ForDietitiansPage() {
  return (
    <>
      <Head>
        <title>Featcal for Dietitians | Better follow-up, less admin</title>
        <meta
          name="description"
          content="Featcal helps dietitians run structured nutrition follow-up: faster food log review, higher adherence between sessions, and better outcomes across weight loss, weight gain, and metabolic health."
        />
      </Head>

      <header className="bg-gradient-to-b from-white from-20% to-blue-300 flex flex-col gap-16 items-center justify-between overflow-hidden px-8 xl:px-0 z-[51] relative pb-20">
        <Navigation />
        <div className="flex flex-col md:items-center md:justify-center gap-6 mt-2 z-10 md:text-center">
          <p className="text-sm font-semibold text-blue-700 bg-white/70 border border-blue-200 px-4 py-2 rounded-full w-fit md:mx-auto">
            Built for dietitians who want focus
          </p>
          <h1 className="text-4xl md:text-5xl font-medium leading-tight max-w-3xl">
            A repeatable follow-up workflow for dietitians.
          </h1>
          <p className="text-md max-w-2xl mx-auto text-slate-700">
            Featcal helps you spend less time on prep, messages, and manual
            analysis-and more time doing what matters: clinical thinking,
            patient care, and outcomes.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 md:justify-center">
            <Link
              href="/start"
              className="bg-blue-600 text-white py-3 px-8 rounded-full font-semibold hover:bg-blue-700 transition-all duration-200 text-center"
            >
              Start for free
            </Link>
            <Link
              href="/#faq"
              className="bg-white text-slate-900 py-3 px-8 rounded-full font-semibold border border-blue-200 hover:bg-blue-50 transition-all duration-200 text-center"
            >
              See FAQs
            </Link>
          </div>
        </div>
      </header>

      <main id="main-content" className="bg-white">
        <section className="py-16 px-8 xl:px-0">
          <div className="max-w-6xl mx-auto bg-white border border-slate-200 rounded-3xl p-8">
            <h2 className="text-2xl md:text-3xl font-semibold text-slate-900 mb-3">
              The real challenge: between-session follow-up
            </h2>
            <ul className="mt-4 space-y-3 text-slate-700">
              {[
                "Food logs are time-consuming to decode.",
                "Plans get lost in messages and PDFs.",
                "Adherence drops between sessions-then sessions become catch-up.",
              ].map((p) => (
                <li key={p} className="flex gap-3">
                  <span className="text-slate-400 font-bold">•</span>
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="py-20 px-8 pt-0 xl:px-0">
          <div className="max-w-6xl mx-auto grid lg:grid-cols-3 gap-8">
            {[
              {
                title: "Review food logs faster",
                desc: "Automatic food log analysis and clean summaries reduce manual prep before sessions.",
              },
              {
                title: "Reduce no-shows and drop-offs",
                desc: "Reminders and in-app plans keep clients on track between sessions-without extra chasing from you.",
              },
              {
                title: "Package a clearer ongoing program",
                desc: "Deliver plans, recipes, progress, and guidance in one place so clients understand the value of follow-up.",
              },
            ].map((card) => (
              <div
                key={card.title}
                className="bg-gradient-to-br from-blue-50 to-white border border-blue-100 rounded-3xl p-8 shadow-xl shadow-blue-100/30"
              >
                <h2 className="text-xl font-semibold text-slate-900 mb-3">
                  {card.title}
                </h2>
                <p className="text-slate-700 leading-relaxed">{card.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="py-20 px-8 xl:px-0 pt-0">
          <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-10 items-start">
            <div>
              <h2 className="text-3xl md:text-4xl font-medium text-slate-900 mb-4">
                A workflow built around outcomes
              </h2>
              <p className="text-slate-700 leading-relaxed mb-8">
                Featcal keeps clients consistent between sessions and gives you
                a fast review flow-so you spend your time on clinical decisions,
                not admin.
              </p>
              <div className="space-y-4">
                {[
                  {
                    title: "1) Onboard in minutes",
                    desc: "Invite a client, set goals, and choose what to track. The app guides them day by day.",
                  },
                  {
                    title: "2) Review in minutes",
                    desc: "See trends and food patterns at a glance-no digging through screenshots.",
                  },
                  {
                    title: "3) Deliver plans that stick",
                    desc: "Plans, recipes, and reminders live in-app-always accessible and up to date.",
                  },
                ].map((s) => (
                  <div
                    key={s.title}
                    className="bg-white border border-slate-200 rounded-2xl p-6"
                  >
                    <p className="font-semibold text-slate-900">{s.title}</p>
                    <p className="text-slate-700 mt-1">{s.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-white border border-blue-100 rounded-3xl p-8 shadow-xl shadow-blue-100/30">
              <h3 className="text-2xl font-semibold text-slate-900 mb-4">
                What you gain quickly
              </h3>
              <ul className="space-y-3 text-slate-700">
                {[
                  "Faster session prep with structured summaries.",
                  "Higher adherence between sessions.",
                  "Fewer routine messages (“what do I do next?”).",
                  "A clearer ongoing offer you can package and sell.",
                ].map((p) => (
                  <li key={p} className="flex gap-3">
                    <span className="text-green-600 font-bold">✓</span>
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <Link
                  href="/start"
                  className="bg-blue-600 text-white py-3 px-8 rounded-full font-semibold hover:bg-blue-700 transition-all duration-200 text-center"
                >
                  Start for free
                </Link>
                <Link
                  href="/#faq"
                  className="bg-white text-slate-900 py-3 px-8 rounded-full font-semibold border border-blue-200 hover:bg-blue-50 transition-all duration-200 text-center"
                >
                  See FAQs
                </Link>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 px-8 xl:px-0 pt-0">
          <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-10 items-start">
            <div className="bg-white border border-slate-200 rounded-3xl p-8">
              <h2 className="text-2xl md:text-3xl font-semibold text-slate-900 mb-4">
                Great fit for
              </h2>
              <ul className="space-y-3 text-slate-700">
                {[
                  "Weight loss / weight gain follow-up with weekly reviews.",
                  "Behavior change programs (protein, meal timing, habits).",
                  "Digestive health with symptom + food patterns.",
                  "Sports nutrition without spreadsheet chaos.",
                ].map((p) => (
                  <li key={p} className="flex gap-3">
                    <span className="text-green-600 font-bold">✓</span>
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-gradient-to-br from-blue-50 to-white border border-blue-100 rounded-3xl p-8 shadow-xl shadow-blue-100/30">
              <h3 className="text-2xl font-semibold text-slate-900 mb-4">
                What Featcal replaces
              </h3>
              <ul className="space-y-3 text-slate-700">
                {[
                  "Screenshots, DMs, and scattered notes.",
                  "Manual food log decoding and spreadsheets.",
                  "Lost meal plans and “can you resend?” requests.",
                  "Chasing adherence between sessions.",
                ].map((p) => (
                  <li key={p} className="flex gap-3">
                    <span className="text-slate-400 font-bold">•</span>
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        <WhoWeServeFeatureSection
          eyebrow="Meal plans"
          title="Plans clients actually follow"
          description="Deliver meal plans in-app so clients always know what to do next-and you spend less time resending PDFs and instructions."
          bullets={[
            "Plans stay up to date in the client app.",
            "Less back-and-forth between sessions.",
            "A clearer follow-up experience that improves retention.",
          ]}
          imageSrc="/images/meal-plan.webp"
          imageAlt="Meal plan preview"
          align="right"
          imageFit="contain"
        />
        <section className="bg-gradient-to-b from-white to-blue-50 py-20 px-8 xl:px-0">
          <div className="max-w-6xl mx-auto bg-white border border-blue-100 rounded-3xl p-8 md:p-10 shadow-xl shadow-blue-100/30">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div>
                <h2 className="text-2xl md:text-3xl font-semibold text-slate-900">
                  Ready to run follow-up with less admin?
                </h2>
                <p className="text-slate-700 mt-2">
                  Start free and invite a client in minutes.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link
                  href="/start"
                  className="bg-blue-600 text-white py-3 px-8 rounded-full font-semibold hover:bg-blue-700 transition-all duration-200 text-center"
                >
                  Start for free
                </Link>
                <Link
                  href="/#faq"
                  className="bg-white text-slate-900 py-3 px-8 rounded-full font-semibold border border-blue-200 hover:bg-blue-50 transition-all duration-200 text-center"
                >
                  See FAQs
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}
